import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Tabs, Tab, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert,
  Chip, CircularProgress, Stack, Select, MenuItem, FormControl, InputLabel, IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { patternTaxonomyApi } from '../../services/api';
import UnmappedPatternsPanel from './UnmappedPatternsPanel';
import AddTypeDialog from './AddTypeDialog';

function PatternTypesPanel({ families, onChange }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [filterFamily, setFilterFamily] = useState('');
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await patternTaxonomyApi.listTypes();
      setTypes(resp.types || []);
    } catch (e) {
      setError(e.message || 'Failed to load patterns');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleReassign = async (normalizedPattern, family) => {
    try {
      setError(null);
      setInfo(null);
      const resp = await patternTaxonomyApi.updateTypeFamily(normalizedPattern, family);
      const updated = resp.result?.rows_updated ?? 0;
      setInfo(`Reassigned "${normalizedPattern}" to family "${family}" (${updated} rows updated)`);
      await load();
      if (onChange) onChange();
    } catch (e) {
      setError(e.message || 'Failed to reassign');
    }
  };

  const filtered = types.filter(t => {
    if (filterFamily && t.family !== filterFamily) return false;
    if (search && !t.normalized_pattern.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>{info}</Alert>}

      <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Family</InputLabel>
          <Select value={filterFamily} label="Family" onChange={(e) => setFilterFamily(e.target.value)}>
            <MenuItem value=""><em>All</em></MenuItem>
            {families.map(f => <MenuItem key={f.id} value={f.family}>{f.family}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" label="Pattern contains..." value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 240 }} />
        {(filterFamily || search) && <Button size="small" onClick={() => { setFilterFamily(''); setSearch(''); }}>Clear</Button>}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {filtered.length} of {types.length} pattern types
        </Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>Add type</Button>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        The closed list of pattern types. The AI maps every photo label to one of these, and the
        overlay's pattern channel compares them by name, so one name per pattern: a variant like
        “small checkered” belongs under “checkered”, not next to it.
      </Alert>

      <AddTypeDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        kind="pattern"
        existing={types.map(t => ({ value: t.normalized_pattern, family: t.family }))}
        families={families.map(f => ({ key: f.family, label: f.family }))}
        onCreate={async (pattern, family) => {
          await patternTaxonomyApi.createType(pattern, family);
          setInfo(`Added pattern type "${pattern}" (${family})`);
          await load();
          if (onChange) onChange();
        }}
      />

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Normalized Pattern</strong></TableCell>
              <TableCell align="right" width={120}><strong>Mapping rows</strong></TableCell>
              <TableCell width={260}><strong>Family</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(t => (
              <TableRow key={t.normalized_pattern}>
                <TableCell><Chip label={t.normalized_pattern} size="small" variant="outlined" /></TableCell>
                <TableCell align="right">{t.row_count}</TableCell>
                <TableCell>
                  <FormControl size="small" fullWidth>
                    <Select
                      value={t.family || ''}
                      onChange={(e) => handleReassign(t.normalized_pattern, e.target.value)}
                      displayEmpty
                    >
                      {!t.family && <MenuItem value=""><em>(unassigned)</em></MenuItem>}
                      {families.map(f => <MenuItem key={f.id} value={f.family}>{f.family}</MenuItem>)}
                    </Select>
                  </FormControl>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={3} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No patterns match filters.</Typography></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function PatternFamiliesPanel({ onChange }) {
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ family: '', description: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await patternTaxonomyApi.listFamilies();
      setFamilies(resp.families || []);
    } catch (e) {
      setError(e.message || 'Failed to load families');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleOpenAdd = () => {
    setEditing(null);
    setFormData({ family: '', description: '' });
    setDialogOpen(true);
  };

  const handleOpenEdit = (f) => {
    setEditing(f);
    setFormData({ family: f.family, description: f.description || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      if (editing) {
        await patternTaxonomyApi.updateFamily(editing.id, formData);
      } else {
        await patternTaxonomyApi.createFamily(formData);
      }
      setDialogOpen(false);
      await load();
      if (onChange) onChange();
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (f) => {
    if (f.pattern_count > 0) {
      window.alert(
        `Cannot delete "${f.family}" — ${f.pattern_count} normalized patterns still reference it. ` +
        `Reassign them first (in the Pattern → Family sub-tab).`
      );
      return;
    }
    if (!window.confirm(`Delete pattern family "${f.family}"?`)) return;
    try {
      setError(null);
      await patternTaxonomyApi.deleteFamily(f.id);
      await load();
      if (onChange) onChange();
    } catch (e) {
      setError(e.message || 'Failed to delete');
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Stack direction="row" spacing={1} sx={{ mb: 2, justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Canonical pattern families. Used by pattern matching to group similar prints.
          Adding a family makes it available in the "Pattern → Family" dropdown.
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>Add Family</Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Family</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
              <TableCell align="right"><strong>Patterns</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {families.map(f => (
              <TableRow key={f.id}>
                <TableCell><Chip label={f.family} size="small" color="primary" /></TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{f.description || '—'}</Typography></TableCell>
                <TableCell align="right">{f.pattern_count}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleOpenEdit(f)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" onClick={() => handleDelete(f)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Pattern Family' : 'Add Pattern Family'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Family name"
            value={formData.family}
            onChange={(e) => setFormData({ ...formData, family: e.target.value })}
            fullWidth margin="normal"
            helperText="Lowercase + underscores, e.g. polka_dot, geometric"
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            fullWidth margin="normal" multiline rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving || !formData.family}>{saving ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default function PatternsTab() {
  const [activeSubTab, setActiveSubTab] = useState(0);
  const [families, setFamilies] = useState([]);

  const loadFamilies = useCallback(async () => {
    try {
      const resp = await patternTaxonomyApi.listFamilies();
      setFamilies(resp.families || []);
    } catch (e) {
      // best-effort; downstream panels handle errors
    }
  }, []);

  useEffect(() => { loadFamilies(); }, [loadFamilies]);

  return (
    <Box>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={activeSubTab} onChange={(_e, v) => setActiveSubTab(v)}>
          <Tab label="Unmapped Patterns" />
          <Tab label="Pattern → Family" />
          <Tab label="Pattern Families" />
        </Tabs>
      </Box>
      {activeSubTab === 0 && <UnmappedPatternsPanel />}
      {activeSubTab === 1 && <PatternTypesPanel families={families} onChange={loadFamilies} />}
      {activeSubTab === 2 && <PatternFamiliesPanel onChange={loadFamilies} />}
    </Box>
  );
}
